/**
 * Tenant ID utilities
 *
 * Prevents accidental propagation of malformed tenant context (e.g. the literal
 * strings "undefined"/"null") into request headers.
 */

import { logger } from '@/utils/logger';
import { isUuid, normalizeTenantId } from '../../../shared/utils/tenant';

/**
 * @deprecated Prefer importing isUuid from shared/utils/tenant (target removal: next major release).
 * @example import { isUuid } from '../../../shared/utils/tenant';
 */
export { isUuid };

/**
 * Reads tenantId from localStorage and returns a valid UUID or null.
 *
 * NOTE: We intentionally treat non-UUID values as invalid, because the backend
 * expects a UUID in X-Tenant-ID.
 */
export const getValidTenantId = (): string | null => {
  if (typeof window === 'undefined') return null;

  let raw: string | null = null;
  try {
    // Only read from 'tenantId' — 'currentTenantId' was a legacy key written
    // by old code; no current production code writes to it, and reading it
    // creates a hidden fallback that survives clearTenantId() calls and causes
    // the stale-tenant retry interceptor to silently re-add the stale header.
    raw = localStorage.getItem('tenantId');
  } catch {
    return null;
  }

  if (!raw) return null;

  const trimmed = raw.trim();

  // Common failure mode: someone did localStorage.setItem('tenantId', String(undefined))
  // or the value got persisted incorrectly.
  if (trimmed === 'undefined' || trimmed === 'null') {
    try {
      localStorage.removeItem('tenantId');
    } catch {
      // best-effort
    }

    logger.warn('[Tenant] Ignoring malformed tenantId in storage', { tenantId: trimmed });
    return null;
  }

  const normalized = normalizeTenantId(trimmed);
  if (!normalized) {
    logger.warn('[Tenant] Ignoring non-UUID tenantId in storage', { tenantId: trimmed });
    return null;
  }

  return normalized;
};
